import React, { useState, useEffect } from "react";
import axios from "axios";
import { InputField, Button } from "@mmfestival/shared-components";

/**
 * VoucherInput
 *
 * - Trim + uppercase voucher code before sending.
 * - Send { code, eventId, kategori } (kategori optional) to backend validate endpoint.
 * - Immediately show server error messages when voucher is invalid.
 * - Show a local success message when voucher applied.
 * - Clear error/message when user edits the code.
 */

export const VoucherInput = ({
  onApplyVoucher,
  disabled,
  eventId,
  kategori,
}) => {
  const [voucherCode, setVoucherCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(""); // errors or success

  useEffect(() => {
    if (message) setMessage("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucherCode]);

  const handleApply = async () => {
    setMessage("");
    const code = String(voucherCode || "")
      .trim()
      .toUpperCase();

    if (!code) {
      setMessage("Please enter a voucher code.");
      return;
    }
    if (disabled) return;

    setLoading(true);
    try {
      const payload = { code, eventId };
      if (kategori) payload.kategori = kategori;

      const resp = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/validate-voucher`,
        payload,
        { timeout: 10000 }
      );

      const raw = resp.data?.discountPercentage;
      const discount = typeof raw === "number" ? raw : Number(raw);

      if (!Number.isInteger(discount)) {
        setMessage("Unexpected server response. Please try again.");
        setLoading(false);
        return;
      }

      // Notify parent to apply voucher (parent handles applying discount)
      if (typeof onApplyVoucher === "function") {
        onApplyVoucher(code, discount);
      }

      // Restore local success message (user-visible feedback)
      setMessage(`Voucher applied successfully! ${discount}% discount`);
    } catch (err) {
      // Show server-provided message immediately if available
      const serverMsg =
        err?.response?.data?.message || err?.response?.data?.error;
      setMessage(serverMsg || "Invalid voucher code.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleApply();
    }
  };

  return (
    <div className="flex flex-col my-4">
      {/* Label */}
      <label className="text-slate-800 font-medium mb-1">Voucher Code</label>

      {/* Input + Button side by side */}
      <div className="flex gap-4">
        <div className="flex-1">
          <InputField
            type="text"
            value={voucherCode}
            onChange={(e) =>
              setVoucherCode(String(e.target.value || "").toUpperCase())
            }
            onKeyDown={handleKeyDown}
            placeholder="Enter voucher code"
            disabled={disabled || loading}
            aria-label="Voucher code"
          />
        </div>
        <Button
          type="button"
          onClick={handleApply}
          disabled={disabled || loading}
        >
          {loading ? "Loading..." : "Apply"}
        </Button>
      </div>

      {/* Message (error in red, success in green) */}
      {message && (
        <p
          className={`mt-4 text-sm ${
            message.toLowerCase().includes("success")
              ? "text-green-600"
              : "text-rose-600"
          }`}
          role="alert"
          aria-live="polite"
        >
          {message}
        </p>
      )}
    </div>
  );
};

export default VoucherInput;
